import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import HelpTooltip from 'help/HelpTooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import ControlledNumericInput from 'ui-library/inputs/ControlledNumericInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import ToggleGroup from 'ui-library/ToggleGroup';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

export enum SequenceSearchType {
  BASES = 'Bases',
  AMINO_ACIDS = 'Amino Acids',
}

interface SearchSequenceAdvancedOptionsProps {
  searchType: SequenceSearchType;
  onChangeSearchType: (searchType: SequenceSearchType) => void;
  searchRegion: string;
  onChangeSearchRegion: (orf: string) => void;
  mismatchesAllowed: number | null;
  onChangeMismatchesAllowed: (mismatches: number | null) => void;
}

export function SearchSequenceAdvancedOptions(props: SearchSequenceAdvancedOptionsProps): JSX.Element {
  const [searchType, setSearchType] = useState(props.searchType);
  const [searchRegion, setSearchRegion] = useState(props.searchRegion);
  const [mismatchesAllowed, setMismatchesAllowed] = useState<number | null>(props.mismatchesAllowed);

  function handleMismatchesAllowedChange(mismatches: number | null): void {
    const nonNegativeMismatches = mismatches && mismatches > 0 ? mismatches : 0;
    setMismatchesAllowed(nonNegativeMismatches);
    props.onChangeMismatchesAllowed(nonNegativeMismatches);
  }

  function incrementMismatchesAllowed(): void {
    handleMismatchesAllowedChange((mismatchesAllowed || 0) + 1);
  }

  function decrementMismatchesAllowed(): void {
    handleMismatchesAllowedChange((mismatchesAllowed || 0) - 1);
  }

  return (
    <>
      <div className="settings-header">
        <span>Advanced Settings</span>
        <Button buttonStyle={ButtonStyle.LINK} className="js-close-adv-settings" text="Close" />
      </div>
      <div className="form-horizontal">
        <div>
          <FieldLabel className="control-label" label="Search" />
        </div>
        <div>
          <ToggleGroup
            options={[
              {
                id: SequenceSearchType.BASES,
                buttonProps: {text: SequenceSearchType.BASES, className: 'js-search-type-bases'},
              },
              {
                id: SequenceSearchType.AMINO_ACIDS,
                buttonProps: {text: SequenceSearchType.AMINO_ACIDS, className: 'js-search-type-amino-acids'},
              },
            ]}
            selection={searchType}
            onChangeSelection={(searchType: SequenceSearchType) => {
              setSearchType(searchType);
              props.onChangeSearchType(searchType);
            }}
            size={ButtonSize.SMALL}
          />
        </div>
        <div className="js-search-region">
          <FieldLabel
            className={classNames('control-label', css(styles.smallMarginRight))}
            label="ORF Region"
          />
          <HelpTooltip
            text="Restricting search to ORFs requires that each result occurs in-frame with an ORF."
            container="body"
            className="js-region-help-enabled"
          />
          <HelpTooltip
            text="Restricting search to a region is disabled on large sequences."
            container="body"
            className="hide js-region-help-disabled"
          />
          <Button
            buttonStyle={ButtonStyle.UNBUTTON}
            icon="wrench"
            className={classNames('js-orf-settings', css(styles.wrenchIcon))}
          />
        </div>
        <div>
          <CheckboxInput
            id="restrictToORF"
            checked={searchRegion === 'orfs'}
            onChange={({target: {checked}}) => {
              const region = checked ? 'orfs' : 'all';
              setSearchRegion(region);
              props.onChangeSearchRegion(region);
            }}
            label="Only show results in-frame with an ORF"
          />
        </div>
        {searchType === SequenceSearchType.BASES && (
          <>
            <div className="js-search-mismatches">
              <FieldLabel
                className={classNames('control-label', css(styles.smallMarginRight))}
                label="Mismatches allowed"
              />
              <HelpTooltip
                text="Maximum number of mismatches tolerated between search query and result."
                container="body"
                className="js-mismatches-help-enabled"
              />
              <HelpTooltip
                text="Quick search with mismatches is disabled on large sequences."
                container="body"
                className="hide js-mismatches-help-disabled"
              />
            </div>
            <div className="row">
              <div className={classNames('col col-xs-5', css(styles.flex))}>
                <Button
                  icon="minus"
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  size={ButtonSize.SMALL}
                  onClick={decrementMismatchesAllowed}
                  className="js-mismatches-controls"
                />
                <ControlledNumericInput
                  min={0}
                  onChange={({target: {value, numericValue}}) => {
                    handleMismatchesAllowedChange(numericValue);
                  }}
                  onKeyDown={(e: ElementKeyboardEvent) => {
                    if (keyboard.isUpKey(e)) {
                      incrementMismatchesAllowed();
                    } else if (keyboard.isDownKey(e)) {
                      decrementMismatchesAllowed();
                    }
                  }}
                  placeholder="0"
                  value={mismatchesAllowed}
                  required={false}
                  validateInteger={true}
                  className={classNames('js-mismatches', css(styles.numberInput))}
                />
                <Button
                  icon="plus"
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  size={ButtonSize.SMALL}
                  onClick={incrementMismatchesAllowed}
                  className="js-mismatches-controls"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  flex: {
    display: 'flex',
    alignItems: 'center',
  },
  numberInput: {
    margin: '0px 5px',
  },
  smallMarginLeft: {
    marginLeft: '5px',
  },
  smallMarginRight: {
    marginRight: '5px',
  },
  wrenchIcon: {
    color: Colors.GRAY_DARK,
  },
});
